import React from 'react'

import { RestrictionType } from '~/client/graph'
import PickerSheetLayout from '~/client/src/shared/components/FullScreenPickerSheet/PickerSheetLayout'
import OptionsHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsHeader'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'

const MAX_VISIBLE_ROWS = 3

export interface IWorkflowChip {
  id: string
  name: string
  iconName?: string
  /** Toggled off in the Workflow Configurator. Still a valid selection — it
   * just can't be created — so chips render it muted rather than hiding it. */
  isInactive?: boolean
}

export function toWorkflowChips(
  ids: string[],
  // PermitType, not a structural subset: it carries `isEnabled` non-optionally,
  // so a resolver that can't answer "is this active?" fails to typecheck rather
  // than silently rendering every chip as active.
  resolveChip: (id: string) => WorkflowType | undefined,
): IWorkflowChip[] {
  // Keep the input id on the chip (chip removal filters the stored id arrays by it), but
  // take the name/icon from the resolver — which may resolve a superseded id to its
  // current version, so the chip shows the latest workflow-type name without losing its key.
  const chips: IWorkflowChip[] = []
  for (const id of ids) {
    const chip = resolveChip(id)
    if (chip) {
      chips.push({
        id,
        name: chip.name,
        iconName: chip.iconName,
        isInactive: chip.isInactive,
      })
    }
  }
  return chips
}

interface IProps {
  label: string
  /** Max duration's value field names its unit; max overlapping is a plain count. */
  restrictionType: RestrictionType
  value: string
  onValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  chipWorkflows: IWorkflowChip[]
  onWorkflowRemove: (id: string) => void
  isWorkflowListOpen: boolean
  onWorkflowListOpenChange: (isOpen: boolean) => void
  workflowList: React.ReactNode
  onSave: () => void
  onCancel: () => void
  /** Popover (default, table) keeps the inline layout; sheet adds header + footer
   * chrome and opens the nested "Applied to" workflow list as a sheet too. */
  layout?: 'popover' | 'sheet'
  /** Header title, e.g. "Edit Max Overlapping": the sheet's header, or a row
   * atop the popover, so creating and editing read apart on every surface. */
  title?: string
}

function WorkflowRestrictionEditor({
  label,
  restrictionType,
  value,
  onValueChange,
  chipWorkflows,
  onWorkflowRemove,
  isWorkflowListOpen,
  onWorkflowListOpenChange,
  workflowList,
  onSave,
  onCancel,
  layout = 'popover',
  title = '',
}: IProps) {
  const isSheet = layout === 'sheet'
  // 36px (lg) footer buttons in the sheet; default (32px) in the table popover.
  const buttonSize = isSheet ? 'lg' : 'default'
  // Restriction values are positive-integer counts (max duration / max
  // overlapping). HTML min={1} doesn't stop a cleared field, and an empty value
  // persists as [""] → later coerced to 0 → rejects every booking. Gate Save so
  // only a positive integer can be committed.
  // A type=number input accepts values like "1e3" and "1.5"
  // (Number.isInteger(Number("1e3")) is even true), which would then be committed
  // as the raw string. Gate on a strict digits-only pattern instead.
  const trimmedValue = value.trim()
  const isValueValid = /^\d+$/.test(trimmedValue) && Number(trimmedValue) >= 1
  // A max duration is stored as a bare number that the booking check reads as
  // hours (no unit is persisted), so the field says so.
  const valueLabel =
    restrictionType === RestrictionType.MaxDuration
      ? `${label} (${Localization.translator.hoursUnit})`
      : label

  const valueField = (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:px-2.5">
      <label className="tw:text-label-lg tw:text-foreground">
        {valueLabel}
      </label>
      <Input
        type="number"
        min={1}
        value={value}
        onChange={onValueChange}
        className="tw:[appearance:textfield] tw:[&::-webkit-inner-spin-button]:appearance-none tw:[&::-webkit-outer-spin-button]:appearance-none"
      />
    </div>
  )

  const appliedTo = (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:px-2.5">
      <label className="tw:text-label-lg tw:text-foreground">Applied to</label>
      <PopoverTagPicker
        isOpen={isWorkflowListOpen}
        onOpen={() => onWorkflowListOpenChange(true)}
        onClose={() => onWorkflowListOpenChange(false)}
        placeholder={Localization.translator.selectWorkflowTypes}
        triggerLabel={Localization.translator.selectWorkflowTypes}
        maxVisibleRows={MAX_VISIBLE_ROWS}
        presentation={layout}
        popoverClassName="tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px]"
        popoverContent={
          // Edge-to-edge: the workflow list owns its Title and applies live (no
          // footer — the editor's own Save/Cancel commits), so the selector owns
          // its insets with no header/padding gutters, matching the Workflow
          // Types cell. The child selector must fill this
          // box ([&>*]:flex-1/min-h-0) so its virtualized list gets a bounded
          // scroll height — without it the list renders zero rows.
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:[&>*]:min-h-0 tw:[&>*]:flex-1">
            {workflowList}
          </div>
        }
      >
        {chipWorkflows.map(workflow => (
          <Tag
            key={workflow.id}
            icon={
              <WorkflowTypeIcon
                iconName={workflow.iconName ?? ''}
                size="small"
                className={workflow.isInactive ? 'tw:opacity-40' : ''}
              />
            }
            onDismiss={() => onWorkflowRemove(workflow.id)}
          >
            <span
              className={
                workflow.isInactive ? 'tw:text-muted-foreground' : undefined
              }
            >
              {workflow.name}
            </span>
          </Tag>
        ))}
      </PopoverTagPicker>
    </div>
  )

  const footerButtons = (
    <>
      <Button variant="outline" size={buttonSize} onClick={onCancel}>
        {Localization.translator.cancel}
      </Button>
      <Button size={buttonSize} onClick={onSave} disabled={!isValueValid}>
        {Localization.translator.save}
      </Button>
    </>
  )

  if (isSheet) {
    return (
      <PickerSheetLayout
        title={title}
        onClose={onCancel}
        heightMode="content"
        footer={
          <div className="tw:flex tw:justify-end tw:gap-2">{footerButtons}</div>
        }
      >
        {/* Top padding on top of the header band's own: the first field sits
            as far from the title as the fields do from each other. */}
        <div className="tw:flex tw:flex-col tw:gap-5 tw:py-2.5">
          {valueField}
          {appliedTo}
        </div>
      </PickerSheetLayout>
    )
  }

  return (
    <div className="tw:flex tw:flex-col">
      {/* The title takes the fields' gap, so the first field doesn't sit
          closer to it than to the next field. */}
      <div className="tw:flex tw:flex-col tw:gap-5 tw:py-2.5">
        {title && (
          <div className="tw:px-2.5">
            <OptionsHeader title={title} onClose={onCancel} />
          </div>
        )}
        {valueField}
        {appliedTo}
      </div>
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:p-3">
        {footerButtons}
      </div>
    </div>
  )
}

export default WorkflowRestrictionEditor
